import React, { createContext, useContext, useState, useEffect } from 'react';

export interface HistorianRecord {
  timestamp: string;
  tagName: string;
  description: string;
  value: number;
  unit: string;
  quality: string;
}

interface HistorianContextType {
  historianData: HistorianRecord[];
  updateHistorianData: (data: HistorianRecord[]) => void;
  getTagValue: (tagName: string) => HistorianRecord | undefined;
  getTagsByPrefix: (prefix: string) => HistorianRecord[];
}

const HistorianContext = createContext<HistorianContextType | null>(null);

export const useHistorian = () => {
  const context = useContext(HistorianContext);
  if (!context) {
    throw new Error('useHistorian must be used within a HistorianProvider');
  }
  return context;
};

export const HistorianProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [historianData, setHistorianData] = useState<HistorianRecord[]>([
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'RW_FLOW_01',
      description: 'Raw Water Flow',
      value: 245.8,
      unit: 'm³/h',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'RW_TURB_01',
      description: 'Raw Water Turbidity',
      value: 12.5,
      unit: 'NTU',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'RW_PH_01',
      description: 'Raw Water pH',
      value: 7.2,
      unit: 'pH',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'COAG_DOSE_01',
      description: 'Coagulant Dosing Rate',
      value: 42.5,
      unit: 'L/h',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'FLOC_SPD_01',
      description: 'Flocculator Speed',
      value: 35,
      unit: 'RPM',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'UF_TMP_01',
      description: 'UF Trans-membrane Pressure',
      value: 0.85,
      unit: 'bar',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'UF_FLUX_01',
      description: 'UF Flux Rate',
      value: 65.2,
      unit: 'LMH',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'RO_FEED_COND_01',
      description: 'RO Feed Conductivity',
      value: 520,
      unit: 'µS/cm',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'RO_PERM_FLOW_01',
      description: 'RO Permeate Flow',
      value: 85.5,
      unit: 'm³/h',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'RO_RECOVERY_01',
      description: 'RO Recovery Rate',
      value: 75.8,
      unit: '%',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'RO_SEC1_PRESS',
      description: 'RO Section 1 Pressure',
      value: 15.2,
      unit: 'bar',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'PROD_COND_01',
      description: 'Product Water Conductivity',
      value: 12.5,
      unit: 'µS/cm',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'PROD_PH_01',
      description: 'Product Water pH',
      value: 6.8,
      unit: 'pH',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'CL2_RESIDUAL_01',
      description: 'Chlorine Residual',
      value: 0.85,
      unit: 'mg/L',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'STORAGE_LEVEL_01',
      description: 'Storage Tank Level',
      value: 82.5,
      unit: '%',
      quality: 'Good'
    },
    // Additional process parameters
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'RW_TEMP_01',
      description: 'Raw Water Temperature',
      value: 22.5,
      unit: '°C',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'RW_COND_01',
      description: 'Raw Water Conductivity',
      value: 850,
      unit: 'µS/cm',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'ANTISCALANT_FLOW_01',
      description: 'Antiscalant Dosing Flow',
      value: 2.5,
      unit: 'L/h',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'RO_FEED_PRESS_01',
      description: 'RO Feed Pressure',
      value: 15.5,
      unit: 'bar',
      quality: 'Good'
    },
    {
      timestamp: '2024-03-14 10:00:00',
      tagName: 'RO_CONC_FLOW_01',
      description: 'RO Concentrate Flow',
      value: 35.5,
      unit: 'm³/h',
      quality: 'Good'
    }
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setHistorianData(prev => prev.map(record => ({
        ...record,
        value: record.value + (Math.random() * 2 - 1) * (record.value * 0.02),
        timestamp: new Date().toISOString().slice(0, 19).replace('T', ' ')
      })));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const updateHistorianData = (data: HistorianRecord[]) => {
    setHistorianData(data);
  };

  const getTagValue = (tagName: string) => {
    return historianData.find(record => record.tagName === tagName);
  };

  const getTagsByPrefix = (prefix: string) => {
    return historianData.filter(record => record.tagName.startsWith(prefix));
  };

  return (
    <HistorianContext.Provider value={{
      historianData,
      updateHistorianData,
      getTagValue,
      getTagsByPrefix
    }}>
      {children}
    </HistorianContext.Provider>
  );
};